import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const TermsReacceptGate = () => {
  const { user, loading: authLoading } = useAuth();
  const [needsAccept, setNeedsAccept] = useState(false);
  const [latestTermsId, setLatestTermsId] = useState<string | null>(null);
  const [latestUpdated, setLatestUpdated] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading || !user) {
      setNeedsAccept(false);
      return;
    }
    (async () => {
      const { data: latest } = await supabase
        .from("terms_of_service")
        .select("id,last_updated,updated_at")
        .eq("is_published", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!latest?.id) return;

      const { data: acceptance } = await supabase
        .from("terms_acceptances")
        .select("terms_id")
        .eq("user_id", user.id)
        .eq("terms_id", latest.id)
        .limit(1)
        .maybeSingle();

      if (!acceptance) {
        setLatestTermsId(latest.id);
        setLatestUpdated(latest.last_updated);
        setAgreed(false);
        setNeedsAccept(true);
      }
    })();
  }, [user, authLoading]);

  const handleAccept = async () => {
    if (!user || !latestTermsId) return;
    setSubmitting(true);
    const { error } = await supabase.from("terms_acceptances").insert({
      user_id: user.id,
      terms_id: latestTermsId,
      accepted_at: new Date().toISOString(),
    });
    setSubmitting(false);
    if (error) {
      toast.error("Failed to record acceptance: " + error.message);
      return;
    }
    try {
      localStorage.setItem("bpr_terms_accepted_at", new Date().toISOString());
    } catch {}
    toast.success("Thanks for accepting the updated Terms of Service.");
    setNeedsAccept(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setNeedsAccept(false);
  };

  return (
    <Dialog open={needsAccept}>
      <DialogContent
        className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Updated Terms of Service</DialogTitle>
          <DialogDescription>
            Our Terms of Service have been updated
            {latestUpdated &&
              ` on ${new Date(latestUpdated).toLocaleDateString()}`}
            . Please review and accept the new Terms to continue using
            BestPriceRide.com.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Link
            to="/terms"
            target="_blank"
            className="text-primary hover:underline text-sm font-medium"
          >
            Read the updated Terms of Service →
          </Link>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox
            id="reaccept-terms"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(v === true)}
            className="mt-1"
          />
          <span className="text-sm text-foreground leading-relaxed">
            I have read and agree to the updated Terms of Service.
          </span>
        </label>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleSignOut} disabled={submitting}>
            Sign out
          </Button>
          <Button onClick={handleAccept} disabled={!agreed || submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Accept & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TermsReacceptGate;
